import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { PackageOption_1_0_1 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import { convertDateByTimezone } from '@luxuryescapes/lib-refunds/build/lib/formatDate'
import { filterDefinedProperties } from 'analytics/snowplow/helpers'
import { getCategoryAndProductId } from 'analytics/snowplow/helpers/itemCategorisation'
import { countOccupants } from 'lib/offer/occupancyUtils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

export const getBedbankRateItemContext = (
  offer: App.BedbankOffer | App.BedbankOfferSummary,
  rate: App.BedbankRate,
  price: number,
  value: number,
  checkIn: string,
  checkOut: string,
  occupancy: Array<App.Occupants>,
  currency: string,
) => {
  const offerId = offer.id
  const rateId = rate.id
  const propertyFees = rate.totals.propertyFees ?? 0
  const {
    adults: numberOfAdults,
    children: numberOfChildren,
    infants: numberOfInfants,
  } = countOccupants(occupancy)
  const { categoryId, productId } = getCategoryAndProductId(offer.parentType, offer)

  return filterDefinedProperties([
    comluxgroup.createItem_1_1_0({
      offerId,
      categoryId,
      productId,
      typeId: rate.roomId,
      price,
      value,
      propertyFees,
      currency,
      travelStart: checkIn,
      travelEnd: checkOut,
      numberOfAdults,
      numberOfChildren,
      numberOfInfants,
      rateId,
    }),
  ])
}

export function getPropertyRateItemContext(
  offer: App.HotelOffer | App.BedbankOffer,
  occupancy: Array<App.Occupants>,
  checkIn: string,
  checkOut: string,
  rate: App.PropertyRate,
  currency: string,
) {
  const offerId = offer.id
  const { categoryId, productId } = getCategoryAndProductId(offer.parentType, offer)
  const {
    adults: numberOfAdults,
    children: numberOfChildren,
    infants: numberOfInfants,
  } = countOccupants(occupancy)

  return filterDefinedProperties([
    comluxgroup.createItem_1_1_0({
      offerId,
      categoryId,
      productId,
      typeId: rate.roomId,
      rateId: getPropertyRateId(rate),
      travelStart: checkIn,
      travelEnd: checkOut,
      numberOfAdults,
      numberOfChildren,
      numberOfInfants,
      // Note that these amounts don't necessarily reflect what's being displayed (as we may choose
      // to show LuxPlus+ pricing). This is something we'll want to address, but for now, the data
      // team are aware of this discrepancy.
      value: rate.total.valuedUpToPrice,
      price: rate.total.displayPrice,
      propertyFees: rate.total.propertyFees,
      currency,
    }),
  ])
}

function getRateListName(type: 'bedbank' | 'property'): string {
  switch (type) {
    case 'bedbank':
      return 'Bedbank'
    case 'property':
      return 'SPP'
    default:
      assertUnreachable(type)
  }
}

export const getRateListContext = (
  type: 'bedbank' | 'property',
  ratePosition: number,
  rateListId: string,
  roomPosition: number,
  roomListId: string,
) => {
  const name = getRateListName(type)
  return filterDefinedProperties([
    comluxgroup.createListItem_1_0_0({
      itemPosition: ratePosition,
      listInstance: rateListId,
      listName: `PackageOptionList - Hotels - ${name} rates`,
    }),
    comluxgroup.createListItem_1_0_0({
      itemPosition: roomPosition,
      listInstance: roomListId,
      listName: `PackageList - Hotels - ${name} rooms`,
    }),
  ])
}

export const getBedbankRateContext = (
  rate: App.BedbankRate,
  isFlightBundle: boolean, // rate.isFlightBundle is not reliable, so we pass it explicitly
) => {
  const rateId = rate.id

  const cancellationPolicy: PackageOption_1_0_1['cancellationPolicy'] = {
    refundable: rate.refundable,
    partiallyRefundable: rate.partiallyRefundable,
    penalties: rate.cancellationPolicy.policies.flatMap((cp) => {
      try {
        const start = convertDateByTimezone(cp.start, cp.timezone).toISOString()
        const end = convertDateByTimezone(cp.end, cp.timezone).toISOString()
        return [{
          start,
          end,
          ...(cp.amount ? { amount: cp.amount } : {}),
          ...(cp.nights ? { nights: cp.nights } : {}),
          ...(cp.percent ? { percent: cp.percent } : {}),
          currency: cp.currency,
        }]
      } catch {
        // can't parse the date, skip this entry
        return []
      }
    }),
  }

  const inclusions = rate.inclusions.map(x => x.description)

  return filterDefinedProperties([
    comluxgroup.createPackageOption_1_0_1({
      id: rateId,
      supplier: rate.supplierCode,
      cancellationPolicy,
      inclusions,
      isFlightBundle,
    }),
  ])
}

function getPropertyRateId(rate: App.PropertyRate) {
  switch (rate.type) {
    case 'lpc':
      return rate.lpcRateId
    case 'bedbank':
      return rate.bedbankRateId
    default:
      assertUnreachable(rate)
  }
}

function getPropertyRateSupplier(rate: App.PropertyRate) {
  switch (rate.type) {
    case 'lpc':
      return 'lpc'
    case 'bedbank':
      return rate.bedbankSupplierCode
    default:
      assertUnreachable(rate)
  }
}

// TODO(Xiao): This needs to be updated once I fix up the PropertyRate type.
export function getPropertyRateContext(rate: App.PropertyRate, currency: string) {
  // We'll order these roughly by how they'd be displayed.
  const allInclusions = [
    rate.luxPlusInclusions,
    rate.bonusInclusions,
    rate.inclusions,
  ].flatMap(inclusions => inclusions.map(({ description }) => description))
  return filterDefinedProperties([
    comluxgroup.createPackageOption_1_0_1({
      id: getPropertyRateId(rate),
      supplier: getPropertyRateSupplier(rate),
      cancellationPolicy: {
        refundable: rate.headlineCancellationPolicyType === 'fullyRefundable',
        partiallyRefundable: rate.headlineCancellationPolicyType === 'partiallyRefundable',
        penalties: rate.cancellationPolicies.map(({ start, end, amount }) => ({
          start,
          end,
          amount,
          currency,
        })),
      },
      inclusions: allInclusions,
      isFlightBundle: rate.includesFlights,
    }),
  ])
}
